import { Search, AlertCircle, Building2, Loader2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { YmsAlertDialog } from '@/components/dashboard/YmsAlertDialog';
import { ROUTES } from '@/constants';
import { usePlantsQuery } from '@/hooks/auth';
import { usePreventBackNavigation } from '@/hooks/usePreventBackNavigation';
import { useRedirectAfterPlantSelection } from '@/hooks/useRedirectAfterPlantSelection';
import { useAppDispatch, useAppSelector, setPlant } from '@/store';
import { clearUnreadNotificationIds, clearLastNotificationTime } from '@/utils';

import type { PlantInfo } from '@/types';

export function PlantSelectionPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // When accessed via /change-plant route, it's from sidebar and MainLayout provides the header
  const isFromSidebar = location.pathname === ROUTES.changePlant;

  const { redirectAfterPlantSelect, isRedirecting } = useRedirectAfterPlantSelection();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const allowNavigationRef = useRef(false);

  const handleBackAttempt = useCallback(() => {
    setShowLogoutDialog(true);
  }, []);

  // Block back navigation and show confirmation modal on /plant-selection (not /change-plant)
  usePreventBackNavigation(!isFromSidebar, allowNavigationRef, handleBackAttempt);

  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const plantsQuery = usePlantsQuery();

  const allPlants = useAppSelector((state) => state.global.plants);
  const userPlants = useAppSelector((state) => state.user.user?.plants);
  const currentAuthPlant = useAppSelector((state) => state.user.plant);

  // Get the base list: user plants initially, all plants after "See All"
  let plants = allPlants;
  if (!showAll && userPlants && userPlants.length > 0) {
    const userPlantIds = userPlants.map((p) => p._id);
    plants = allPlants.filter((p) => userPlantIds.includes(p._id));
  }

  // Apply search filter
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim();
    plants = plants.filter((p) => p.plant_name.toLowerCase().includes(q));
  }

  const isLoading = plantsQuery.isLoading || (plantsQuery.isFetching && allPlants.length === 0);
  const hasMorePlants =
    !showAll && userPlants && userPlants.length > 0 && allPlants.length > userPlants.length;

  const handlePlantSelect = useCallback(
    (plant: PlantInfo) => {
      if (isRedirecting) return;
      clearUnreadNotificationIds();
      clearLastNotificationTime();
      dispatch(setPlant(plant));

      if (isFromSidebar) {
        navigate(ROUTES.workModeSelection);
      } else {
        void redirectAfterPlantSelect(plant);
      }
    },
    [dispatch, navigate, isFromSidebar, isRedirecting, redirectAfterPlantSelect]
  );

  return (
    <div className={isFromSidebar ? 'flex flex-col' : 'min-h-screen flex flex-col'}>
      {/* Header Section - Only shown when NOT from sidebar (after OTP) */}
      {!isFromSidebar && (
        <div className="bg-white pt-4">
          <div className="flex flex-col items-center px-[10px] md:px-[30px]">
            <img
              src="/images/plant_header.svg"
              alt=""
              className="h-[35px] w-auto mb-2"
              aria-hidden="true"
            />
            <p className="text-[#0E3968] text-lg font-semibold text-center">
              {t('plant.selection.subtitle', 'Please select the plant and continue')}
            </p>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="bg-white sticky top-0 z-10">
        <div className="py-4 px-[10px] md:px-[30px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('plant.selection.search')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              className="w-full h-10 pl-10 pr-4 border border-gray-300 rounded-md bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1457A0] focus:border-transparent transition-all text-sm"
            />
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 py-4 px-[10px] md:px-[30px]">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-12 w-12 text-[#1457A0] animate-spin mb-4" />
            <p className="text-gray-500">{t('plant.selection.loading', 'Loading plants...')}</p>
          </div>
        )}

        {/* Empty State - no results from search */}
        {!isLoading && plants.length === 0 && allPlants.length > 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <Building2 className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('plant.selection.noResults', 'No plants found')}
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              {t('plant.selection.noResultsDescription', 'Try adjusting your search criteria.')}
            </p>
          </div>
        )}

        {/* No Plants Available State */}
        {!isLoading && allPlants.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <AlertCircle className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('plant.selection.noPlants', 'No plants available')}
            </h3>
            <p className="text-gray-500 text-center max-w-md">
              {t(
                'plant.selection.noPlantsDescription',
                'You do not have access to any plants. Please contact your administrator.'
              )}
            </p>
          </div>
        )}

        {/* Plant Cards Grid */}
        {!isLoading && plants.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {plants.map((plant) => {
              const isSelected = plant._id === currentAuthPlant?._id;
              return (
                <div
                  key={plant._id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    handlePlantSelect(plant);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handlePlantSelect(plant);
                    }
                  }}
                  aria-pressed={isSelected}
                  aria-label={`Select ${plant.plant_name}`}
                  className={`relative h-[150px] rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1457A0] bg-gradient-to-r from-[#0B2336] from-10% to-[#093F73] to-100% hover:shadow-lg ${isSelected ? 'ring-2 ring-[#1457A0]' : ''}`}
                >
                  <div className="p-4 h-full flex flex-col items-center justify-center gap-[30px]">
                    <img src="/images/plant.svg" alt="" className="h-10 w-10" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-white text-center line-clamp-2">
                      {plant.plant_name}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* See All Button */}
      {!isLoading && hasMorePlants && (
        <div className="fixed bottom-0 left-0 right-0 bg-white py-2.5 z-20">
          <div className="text-center">
            <button
              onClick={() => {
                setShowAll(true);
              }}
              className="text-[#0E3968] text-sm underline focus:outline-none focus:ring-2 focus:ring-[#1457A0] focus:ring-offset-2 rounded px-4 py-2"
            >
              {t('plant.selection.seeAll', 'See All')}
            </button>
          </div>
        </div>
      )}

      {/* Spacer for fixed See All button */}
      {!isLoading && hasMorePlants && <div className="h-16" />}

      {isRedirecting && (
        <div className="fixed inset-0 z-50 bg-white/80 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 text-[#1457A0] animate-spin" />
            <p className="text-gray-600 font-poppins text-sm">
              {t('plant.selection.redirecting', 'Loading your session...')}
            </p>
          </div>
        </div>
      )}

      {!isFromSidebar && (
        <YmsAlertDialog
          open={showLogoutDialog}
          title={t('common.exitConfirmTitle', 'Exit Application')}
          message={t('common.exitConfirmMessage', 'Are you sure you want to exit the application?')}
          okText={t('common.yes', 'Yes')}
          cancelText={t('common.no', 'No')}
          showCancel
          onOk={() => {
            allowNavigationRef.current = true;
            setShowLogoutDialog(false);
            navigate(ROUTES.logoutSummary);
          }}
          onCancel={() => {
            setShowLogoutDialog(false);
          }}
        />
      )}
    </div>
  );
}
